import React, { useEffect, useState, useCallback, useRef } from "react"
import { Helmet } from "react-helmet"
import { Box, Button, TextField, CircularProgress, Chip } from "@mui/material"
import APIS from "../../../utils/APIS"

/* Espace Support (admin) — la boite de reception du chat.
 *
 * Colonne gauche : les conversations, escaladees et en attente d'abord, triees
 * par urgence. Colonne droite : le fil selectionne et le champ de reponse.
 *
 * La liste se rafraichit toute seule (15 s) pour faire remonter les nouvelles
 * demandes sans recharger la page. La conversation ouverte se rafraichit plus
 * vite (6 s) pour voir arriver un nouveau message de la personne pendant
 * qu'on lui repond.
 */

const RAFRAICHIR_LISTE = 15000
const RAFRAICHIR_FIL = 6000

const COULEUR_URGENCE = { haute: "#c0392b", normale: "#d98a1f", basse: "#7f8c8d" }
const LIBELLE_STATUT = { escalade: "À traiter", ouverte: "En cours", traitee: "Traitée" }

/* Les deux produits arrivent dans la meme boite : un client SCAN et un
 * apprenant LAB n'ont ni les memes questions ni le meme interlocuteur. Le
 * filtre permet de traiter un produit a la fois. `null` = les deux. */
const PLATEFORMES = [
  { valeur: null, libelle: "Tout" },
  { valeur: "lab", libelle: "LAB" },
  { valeur: "scan", libelle: "SCAN" },
]

const dateCourte = (s) => {
  try {
    return new Date(s).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
  } catch (e) { return "" }
}

const Support = () => {
  const [conversations, setConversations] = useState([])
  const [enAttente, setEnAttente] = useState(0)
  const [selection, setSelection] = useState(null)
  const [fil, setFil] = useState({ conversation: null, messages: [] })
  const [reponse, setReponse] = useState("")
  const [envoiEnCours, setEnvoiEnCours] = useState(false)
  const [plateforme, setPlateforme] = useState(null)
  const finDuFil = useRef(null)

  const chargerListe = useCallback(async () => {
    const r = await APIS.supportAdminListe(plateforme ? { plateforme } : {})
    if (r && r.isOk) {
      setConversations(r.conversations || [])
      setEnAttente(r.enAttente || 0)
    }
  }, [plateforme])

  const chargerFil = useCallback(async (id) => {
    if (!id) return
    const r = await APIS.supportAdminConversation({ conversationID: id })
    if (r && r.isOk) setFil({ conversation: r.conversation, messages: r.messages || [] })
  }, [])

  useEffect(() => { chargerListe() }, [chargerListe])

  useEffect(() => {
    const timer = setInterval(chargerListe, RAFRAICHIR_LISTE)
    return () => clearInterval(timer)
  }, [chargerListe])

  useEffect(() => {
    if (!selection) return undefined
    chargerFil(selection)
    const timer = setInterval(() => chargerFil(selection), RAFRAICHIR_FIL)
    return () => clearInterval(timer)
  }, [selection, chargerFil])

  useEffect(() => {
    if (finDuFil.current) finDuFil.current.scrollIntoView({ behavior: "smooth" })
  }, [fil])

  const envoyerReponse = async () => {
    const texte = reponse.trim()
    if (!texte || !selection || envoiEnCours) return
    setEnvoiEnCours(true)
    const r = await APIS.supportAdminRepondre({ conversationID: selection, message: texte })
    if (r && r.isOk) {
      setReponse("")
      await chargerFil(selection)
      chargerListe()
    }
    setEnvoiEnCours(false)
  }

  const marquerTraitee = async () => {
    if (!selection) return
    await APIS.supportAdminTraiter({ conversationID: selection })
    chargerListe()
    chargerFil(selection)
  }

  const roleLibelle = (role) =>
    role === "user" ? "Utilisateur" : role === "humain" ? "Vous (équipe)" : "Assistant"

  return (
    <>
      <Helmet><title>Support — Rising Up</title></Helmet>
      <Box style={{ display: "flex", height: "calc(100vh - 120px)", gap: 16, padding: 16 }}>

        {/* Colonne gauche : la liste */}
        <Box style={{
          width: 340, minWidth: 300, display: "flex", flexDirection: "column",
          border: "1px solid #ECE7E3", borderRadius: 10, overflow: "hidden", backgroundColor: "#fff",
        }}>
          <Box style={{ padding: "12px 16px", borderBottom: "1px solid #ECE7E3", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 600 }}>Conversations</span>
            {enAttente > 0 && (
              <Chip size="small" label={`${enAttente} en attente`} style={{ backgroundColor: "#000", color: "#fff" }} />
            )}
          </Box>
          <Box style={{ display: "flex", gap: 6, padding: "8px 16px", borderBottom: "1px solid #ECE7E3" }}>
            {PLATEFORMES.map((p) => (
              <Chip
                key={p.libelle}
                size="small"
                label={p.libelle}
                onClick={() => setPlateforme(p.valeur)}
                style={{
                  cursor: "pointer",
                  backgroundColor: plateforme === p.valeur ? "#000" : "#F4F1EC",
                  color: plateforme === p.valeur ? "#fff" : "#565656",
                }}
              />
            ))}
          </Box>
          <Box style={{ flex: 1, overflowY: "auto" }}>
            {conversations.length === 0 && (
              <Box style={{ padding: 24, color: "#565656", fontSize: 14 }}>
                {plateforme
                  ? `Aucune conversation ${plateforme.toUpperCase()} pour le moment.`
                  : "Aucune conversation pour le moment."}
              </Box>
            )}
            {conversations.map((c) => (
              <Box
                key={c.id}
                onClick={() => setSelection(c.id)}
                style={{
                  padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid #F4F1EC",
                  backgroundColor: selection === c.id ? "#FAF8F6" : "#fff",
                  borderLeft: selection === c.id ? "3px solid #000" : "3px solid transparent",
                }}
              >
                <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {c.name || "Visiteur non connecté"}
                  </span>
                  <span style={{ fontSize: 11, color: "#999" }}>{dateCourte(c.updatedAt)}</span>
                </Box>
                <div style={{ fontSize: 12, color: "#565656", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.dernierMessage || "…"}
                </div>
                <Box style={{ display: "flex", gap: 6, marginTop: 5, alignItems: "center" }}>
                  <span style={{ fontSize: 10, textTransform: "uppercase", color: "#999" }}>{c.plateforme}</span>
                  <span style={{ fontSize: 10, color: COULEUR_URGENCE[c.urgence] || "#999", fontWeight: 600 }}>
                    {c.urgence}
                  </span>
                  <span style={{ fontSize: 10, color: "#999" }}>· {LIBELLE_STATUT[c.statut] || c.statut}</span>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Colonne droite : le fil */}
        <Box style={{
          flex: 1, display: "flex", flexDirection: "column",
          border: "1px solid #ECE7E3", borderRadius: 10, overflow: "hidden", backgroundColor: "#fff",
        }}>
          {!selection ? (
            <Box style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#999" }}>
              Sélectionnez une conversation à gauche.
            </Box>
          ) : (
            <>
              <Box style={{ padding: "12px 16px", borderBottom: "1px solid #ECE7E3", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>
                      {fil.conversation?.name || "Visiteur non connecté"}
                    </span>
                    {/* Le fil reste ouvert quand on change de filtre, pour ne pas
                      * perdre une réponse en cours d'écriture. Rappeler ici son
                      * produit évite de croire qu'on répond à l'autre. */}
                    {fil.conversation?.plateforme && (
                      <Chip
                        size="small"
                        label={fil.conversation.plateforme.toUpperCase()}
                        style={{ backgroundColor: "#F4F1EC", color: "#565656", fontSize: 10, height: 18 }}
                      />
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: "#565656" }}>
                    {fil.conversation?.email || "aucun email — impossible de recontacter par mail"}
                    {fil.conversation?.type ? ` · ${fil.conversation.type}` : ""}
                  </div>
                </Box>
                {fil.conversation?.statut !== "traitee" && (
                  <Button size="small" variant="outlined" onClick={marquerTraitee}>
                    Marquer traitée
                  </Button>
                )}
              </Box>

              <Box style={{ flex: 1, overflowY: "auto", padding: 16, backgroundColor: "#FAF8F6" }}>
                {fil.messages.map((m) => (
                  <Box key={m.id} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#999", marginBottom: 2 }}>
                      {roleLibelle(m.role)} · {dateCourte(m.createdAt)}
                    </div>
                    <Box style={{
                      display: "inline-block", maxWidth: "80%", padding: "8px 12px", borderRadius: 10, fontSize: 14,
                      whiteSpace: "pre-wrap", wordBreak: "break-word",
                      backgroundColor: m.role === "humain" ? "#000" : "#fff",
                      color: m.role === "humain" ? "#fff" : "#0A0A0B",
                      border: m.role === "humain" ? "none" : "1px solid #ECE7E3",
                    }}>
                      {m.contenu}
                    </Box>
                  </Box>
                ))}
                <div ref={finDuFil} />
              </Box>

              <Box style={{ display: "flex", gap: 8, padding: 12, borderTop: "1px solid #ECE7E3", alignItems: "flex-end" }}>
                <TextField
                  fullWidth multiline maxRows={4} size="small" variant="outlined"
                  placeholder="Votre réponse…"
                  value={reponse}
                  onChange={(e) => setReponse(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); envoyerReponse() } }}
                />
                <Button
                  variant="contained"
                  onClick={envoyerReponse}
                  disabled={envoiEnCours || !reponse.trim()}
                  style={{ backgroundColor: "#000", color: "#fff", minWidth: 100 }}
                >
                  {envoiEnCours ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Répondre"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  )
}

export default Support
